import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuthStore } from '../../../shared/stores/useAuthStore';
import { OperationProgressPlaceholder } from '../../../shared/ui/progress';
import { useAdminPolling } from '../hooks/useAdminPolling';
import { useSuperAdminStore } from '../store/useSuperAdminStore';
import AdminCompaniesRoute from './AdminCompaniesRoute';
import AdminDemoLeadsRoute from './AdminDemoLeadsRoute';
import AdminDocumentsRoute from './AdminDocumentsRoute';
import AdminLicensesRoute from './AdminLicensesRoute';
import AdminUsersRoute from './AdminUsersRoute';
import RequestsRoute from './RequestsRoute';
import AdminShell, { type AdminShellCounts } from './AdminShell';
import DashboardStats from './DashboardStats';
import { AdminButton, AdminToastProvider } from './components';
import './styles/adminTokens.css';
import './styles/adminShell.css';

const DashboardRoute: React.FC = () => {
  const dashboard = useSuperAdminStore((state) => state.dashboard);
  const health = useSuperAdminStore((state) => state.health);
  const expiringLicenses = useSuperAdminStore((state) => state.expiringLicenses);
  const latestTickets = useSuperAdminStore((state) => state.latestTickets);
  const loading = useSuperAdminStore((state) => state.loading);
  const error = useSuperAdminStore((state) => state.error);
  const stale = useSuperAdminStore((state) => state.stale);
  const refreshDashboard = useSuperAdminStore((state) => state.refreshDashboard);

  if (!dashboard && !error) {
    return (
      <section className="admin-dashboard-loading" role="status" aria-busy="true">
        <OperationProgressPlaceholder
          compact
          model={{
            title: 'Loading admin dashboard',
            message: 'Fetching the latest licensing and support overview',
            detail: 'The last available data remains visible during later refreshes.',
            status: 'running',
            percent: null,
            phase: 'Loading admin data',
            locked: true,
          }}
        />
      </section>
    );
  }

  if (!dashboard) {
    return (
      <section
        className="admin-route-placeholder"
        role="alert"
      >
        <div className="admin-route-placeholder__icon" aria-hidden="true" />
        <h2>Overview</h2>
        <p>Unable to load the dashboard: {error}</p>
        <AdminButton
          className="admin-access__submit admin-route-placeholder__retry"
          onClick={refreshDashboard}
          variant="primary"
        >
          Try again
        </AdminButton>
      </section>
    );
  }

  return (
    <DashboardStats
      disambiguateMetricLinks
      error={error}
      expiringLicenses={expiringLicenses}
      health={health ?? { licenseServer: 'offline' }}
      latestTickets={latestTickets}
      loading={loading}
      now={new Date()}
      retry={refreshDashboard}
      stale={stale}
      stats={dashboard}
    />
  );
};

const SuperAdminPage: React.FC = () => {
  const user = useAuthStore((state) => state.user);
  const dashboard = useSuperAdminStore((state) => state.dashboard);
  const health = useSuperAdminStore((state) => state.health);
  const refreshDashboard = useSuperAdminStore((state) => state.refreshDashboard);

  useAdminPolling(refreshDashboard);

  const counts: AdminShellCounts = {
    companies: dashboard?.totalCompanies ?? 0,
    errorReports: dashboard?.openErrorReports ?? 0,
    licenses: dashboard?.totalLicenses ?? 0,
    newRequests: dashboard?.newTickets ?? 0,
    requests: dashboard?.unresolvedTickets ?? 0,
    users: dashboard?.totalUsers ?? 0,
  };

  return (
    <AdminToastProvider>
      <AdminShell
        counts={counts}
        health={health?.licenseServer ?? 'offline'}
        identity={user?.username ?? 'Super administrator'}
      >
        <Routes>
          <Route index element={<Navigate replace to="dashboard" />} />
          <Route path="dashboard" element={<DashboardRoute />} />
          <Route path="licenses" element={<AdminLicensesRoute />} />
          <Route path="users" element={<AdminUsersRoute />} />
          <Route path="companies" element={<AdminCompaniesRoute />} />
          <Route path="demo-leads" element={<AdminDemoLeadsRoute />} />
          <Route path="documents" element={<AdminDocumentsRoute />} />
          <Route path="requests" element={<RequestsRoute />} />
          <Route path="*" element={<Navigate replace to="/super-admin/dashboard" />} />
        </Routes>
      </AdminShell>
    </AdminToastProvider>
  );
};

export default SuperAdminPage;
